// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.

import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiClient } from '../services/api-client.js';
import { useLocale } from '../hooks/useLocale.js';
import { usePageTitle } from '../hooks/usePageTitle.js';
import { useAuth } from '../contexts/AuthContext.js';
import { extractErrorMessage } from '../utils/api-helpers.js';
import { downloadBackupCodesFile } from '../utils/mfa-backup-codes.js';
import { safeNextPath } from '../utils/safe-next-path.js';

/**
 * Sprint 63.7.0 — the forced-enrolment interstitial.
 *
 * ## Why an interstitial and not a grace period or a lockout
 *
 * Three designs were weighed when an organisation escalates to
 * `admin_required` or `all_required` and somebody bound by it has no second
 * factor:
 *
 * 1. **A grace period.** A second stored clock that every login must read and
 *    expire correctly — and a deadline the platform gets wrong is a setting
 *    nothing enforces, which is the exact defect idea-catalog #291 is about.
 * 2. **An immediate lockout.** An availability failure whose remedy needs
 *    another human, on a tenant that has just tried to make itself safer.
 * 3. **This.** The person still signs in and still gets a session. The session
 *    carries `mfa_enrolment_pending`, and `authenticate` admits only six exact
 *    paths: enrol, confirm, verify, status, `/auth/me` and logout. Everything
 *    else is 403 `MFA_ENROLMENT_REQUIRED`.
 *
 * The third is enforcement with a path out the user walks unaided, so **no
 * organisation can lock itself out by escalating**. That is AC13, and it is
 * tested with a T3-role user holding no `platform:mfa:*` permission at all —
 * which is why the six self-service MFA routes lost their permission gates in
 * this same sprint.
 *
 * ## Why this page does its own enrol/confirm rather than redirecting
 *
 * `/admin/security/mfa` is not reachable from a held session: the route that
 * renders it needs reads this session cannot make. Sending the user there would
 * be naming a remedy the product does not offer, which is the failure mode
 * Sprint 61.0.11 spent fourteen review passes on. (This comment first cited
 * "Sprint 63.0.11", a sprint that does not exist; the fourteen-pass review is
 * 61.0.11, corrected in Sprint 63.6.3.)
 */
export function MfaEnrolmentRequiredPage() {
  const { t } = useLocale();
  usePageTitle(t.login.mfa_enrolment_required_title);
  const navigate = useNavigate();
  const { logout } = useAuth();
  // Gate E12.5 pass 1 (P2-7): where the person was going before they were held
  // here (the OAuth consent request, say), honoured only as a same-origin path.
  const [searchParams] = useSearchParams();
  const continueTo = safeNextPath(searchParams.get('next')) ?? '/admin';

  const [enrollment, setEnrollment] = useState<{
    secret: string;
    otpauth_uri: string;
    qr_code_data_url: string;
  } | null>(null);
  const [code, setCode] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function handleStart() {
    setBusy(true);
    setError('');
    try {
      setEnrollment(await apiClient.enrollMfa());
    } catch (err: unknown) {
      setError(extractErrorMessage(err, t.platform_pages.security.mfa_enforcement_save_failed));
    } finally {
      setBusy(false);
    }
  }

  async function handleConfirm(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const result = await apiClient.confirmMfa({ code });
      /*
       * The backup codes are shown ONCE, here, and the user is not moved on
       * until they dismiss them. Navigating straight to `/admin` on success
       * would destroy the only copy of ten recovery codes the server will never
       * show again — and this is the one screen where the user has no other
       * factor to fall back on if they lose the one they just made.
       */
      setBackupCodes(result.backup_codes ?? []);
      setEnrollment(null);
      setCode('');
    } catch (err: unknown) {
      setError(extractErrorMessage(err, t.login.invalid_mfa));
    } finally {
      setBusy(false);
    }
  }

  /*
   * fix-sprint-20260921-2110 Finding 25 — this page is otherwise exemplary:
   * every on-screen string goes through `t`. The downloaded file was the one
   * surface that escaped, and it was the copy-paste twin of the one in
   * `MfaSettingsPage`. One helper, three keys, and a formatted timestamp
   * instead of a raw ISO instant.
   */
  function downloadBackupCodes(codes: string[]) {
    downloadBackupCodesFile(codes, {
      heading: t.platform_pages.security.mfa_backup_codes_file_heading,
      notice: t.platform_pages.security.mfa_backup_codes_file_notice,
      generated: t.platform_pages.security.mfa_backup_codes_file_generated,
    });
  }

  return (
    <div className="dm-page">
      <div className="dm-header">
        <h1>{t.login.mfa_enrolment_required_title}</h1>
        <p>{t.login.mfa_enrolment_required_intro}</p>
      </div>

      {error && (
        <p className="dm-alert dm-alert--danger" role="alert">
          {error}
        </p>
      )}

      {backupCodes && (
        <div className="dm-form">
          {/*
           * fix-sprint-20260921-2110 Finding 9 — the heading used to be
           * `mfa_admin_backup_codes_remaining` ("{count} unused backup codes"),
           * a count borrowed from the administrator's read-only view of
           * somebody else's account, and NOTHING on the screen told the person
           * to save codes the server will never show again. That instruction
           * existed only inside the downloaded file, which they have not
           * downloaded yet at the moment they need to read it.
           */}
          <h2>{t.platform_pages.security.mfa_backup_codes_heading}</h2>
          <p>{t.platform_pages.security.mfa_backup_codes_save_now}</p>
          <ul className="dm-font-mono">
            {backupCodes.map((c) => (
              <li key={c}>{c}</li>
            ))}
          </ul>
          <button type="button" onClick={() => downloadBackupCodes(backupCodes)}>
            {t.common.download}
          </button>
          <button
            type="button"
            onClick={() => {
              /*
               * A full reload, not `navigate('/admin')`. The SPA is holding an
               * auth context built from the login response, which still says
               * the enrolment is pending; re-entering through the router would
               * route straight back here.
               *
               * The claim this comment rests on — that the flag is already
               * cleared server-side — was FALSE when this page shipped and is
               * true now. Verified in the tree on 2026-09-23, not remembered:
               * `POST /api/v1/mfa/confirm` (`modules/mfa/routes.ts`) calls
               * `clearSessionMfaEnrolmentPending(token)`, which reaches
               * `SessionStore.clearMfaEnrolmentPending` in `middleware/auth.ts`
               * and sets `mfa_enrolment_pending: false` together with
               * `mfa_verified: true` across the whole token family. If that
               * call is ever removed, this button becomes a reload that lands
               * the user back on this page — so the two move together.
               */
              window.location.assign(continueTo);
            }}
          >
            {t.login.mfa_enrolment_required_continue}
          </button>
        </div>
      )}

      {!backupCodes && !enrollment && (
        <div className="dm-form">
          <p>{t.login.mfa_enrolment_required_blocked}</p>
          <button type="button" onClick={handleStart} disabled={busy}>
            {busy ? t.common.processing : t.platform_pages.security.mfa_enable}
          </button>
        </div>
      )}

      {enrollment && (
        <div className="dm-form">
          <p>{t.platform_pages.security.mfa_setup}</p>
          {/*
           * fix-sprint-20260921-2110 Finding 9 — one string, `mfa_setup`, used
           * to name all three of these: the paragraph, the image and the field
           * holding the shared secret. The field announced "Scan this QR code
           * with your authenticator app, read only edit text", with nothing
           * saying it is the key; the alt merely repeated the paragraph above
           * it instead of pointing a non-sighted user at that key. It is the
           * only screen a held session can reach, so there is no other surface
           * to fall back on.
           */}
          {enrollment.qr_code_data_url && (
            <img
              src={enrollment.qr_code_data_url}
              alt={t.platform_pages.security.mfa_qr_alt}
              width={200}
              height={200}
            />
          )}
          <div className="dm-form-field">
            <label htmlFor="enrolment-secret">
              {t.platform_pages.security.mfa_secret_manual_label}
            </label>
            <input
              id="enrolment-secret"
              type="text"
              readOnly
              value={enrollment.secret}
              className="dm-w-full dm-font-mono"
              onClick={(e) => (e.target as HTMLInputElement).select()}
            />
          </div>
          <form onSubmit={handleConfirm}>
            <div className="dm-form-field">
              <label htmlFor="enrolment-code">{t.login.mfa_code}</label>
              <input
                id="enrolment-code"
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                required
                aria-required="true"
                disabled={busy}
              />
              <small>{t.login.mfa_instruction}</small>
            </div>
            <button type="submit" disabled={busy || code.length !== 6}>
              {busy ? t.common.processing : t.common.confirm}
            </button>
          </form>
        </div>
      )}

      {/*
       * Always available, in every state.
       *
       * A person who cannot proceed must always be able to leave — and
       * `POST /api/v1/auth/logout` is one of the six paths `authenticate`
       * admits while the hold is on, precisely so this button cannot be a
       * control that does nothing.
       */}
      <button
        type="button"
        className="dm-badge-danger"
        onClick={() => {
          logout();
          navigate('/login');
        }}
      >
        {t.login.mfa_enrolment_required_sign_out}
      </button>
    </div>
  );
}
